import { useEffect, useState } from 'react';
import { HiBell } from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';

interface NotificationToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose?: () => void;
  index?: number;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  message,
  type = 'info', 
  duration = 3000,
  onClose,
  index = 0
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isBellHovered, setIsBellHovered] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
    }, 300);
  };

  const handleBellClick = () => {
    window.location.href = '/notification';
  };

  const getToastStyles = () => {
    const baseStyles = "transform transition-all duration-300 ease-out";
    const visibilityStyles = isVisible 
      ? "translate-x-0 opacity-100 scale-100" 
      : "translate-x-full opacity-0 scale-95";
    
    const toastStyles = `
      bg-white dark:bg-[#111928] 
      border border-gray-200 dark:border-gray-700 
      text-gray-700 dark:text-gray-200 
      shadow-lg rounded-xl
      backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95
      ${isExiting ? 'animate-slide-out' : 'animate-slide-in'}
    `;

    return `${baseStyles} ${visibilityStyles} ${toastStyles}`;
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return 'border-l-4 border-l-green-500';
      case 'error':
        return 'border-l-4 border-l-red-500';
      case 'info':
      default:
        return 'border-l-4 border-l-blue-500';
    }
  };

  return (
    <div 
      className={getToastStyles()}
      style={{
        animationDelay: `${index * 100}ms`,
        transform: `translateY(${index * 8}px)`
      }}
    >
      <div className={`flex items-center p-4 rounded-xl max-w-md relative group ${getTypeStyles()}`}>
        <div 
          className="flex-shrink-0 mr-3 cursor-pointer"
          onClick={handleBellClick}
          onMouseEnter={() => setIsBellHovered(true)}
          onMouseLeave={() => setIsBellHovered(false)}
        >
          <div className="relative">
            <HiBell 
              className={`w-5 h-5 text-blue-500 dark:text-blue-400 transition-all duration-300
                ${isBellHovered ? 'scale-110 rotate-12' : ''}`} 
            />
            <div className={`absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full 
              ${isBellHovered ? 'animate-ping' : 'animate-pulse'}`}>
            </div>
          </div>
        </div>
        <p className="text-sm font-medium truncate max-h-20 overflow-hidden pr-6 flex-grow">
          {message}
        </p>
        <button
          onClick={handleClose}
          className="p-1.5 rounded-full 
                   text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300
                   hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200
                   opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none
                   hover:scale-110 active:scale-95"
          aria-label="Close notification"
        >
          <IoClose className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default NotificationToast; 